import { Injectable, Optional } from '@angular/core';
import { IPSObservabilityService } from '@ips/common-angular';
import { ClaimDocumentItemList, ClaimItem } from 'com.trumbull.phx.claim.api-client';
import { DocumentItemList1, DocumentListReference, DocumentService } from 'com.trumbull.phx.document.api-client';
import { OrderItem } from 'com.trumbull.phx.order.api-client';
import { RecordRetrievalRequestItem } from 'com.trumbull.phx.recordretrievalobjective.api-client';
import * as _ from 'lodash';
import { catchError, finalize, forkJoin, map, Observable, of } from 'rxjs';
import { RootObject } from '../../orders/order-details/orders.model';
import { DocumentReference, RecordDetails, TableData } from '../../orders/order-details/record-index-table/record-index-table.component';

@Injectable({
  providedIn: 'root',
})
export class DocumentProcessingService {
  constructor(private documentService: DocumentService, @Optional() private readonly observability: IPSObservabilityService) { }


  getClaimDocuments(claim: ClaimItem) {
    return [
      ...this.getClaimLevelDocuments(claim),
      ...this.getDocumentListsFromFeatures(claim)
    ];
  }

  private getClaimLevelDocuments(claim: ClaimItem) {
    return claim.documentLists || [];
  }

  private getDocumentListsFromFeatures(claim: ClaimItem) {
    return claim.features?.flatMap(feature => feature.documentLists || []) || [];
  }

  getDocumentListObservables(allDocumentLists: ClaimDocumentItemList[]) {
    return allDocumentLists
      .filter(list => typeof list?.documentListId === 'string')
      .map(documentList =>
        this.documentService.getDocumentList('standard', documentList.documentListId!).pipe(
          catchError(error => {
            console.error(`Error fetching document list ${documentList.documentListId}:`, error);
            return of(undefined);
          })
        )
      );
  }

  filterDocumentReferences(
    documentReferences: (DocumentItemList1 | undefined)[],
    documentPattern: RegExp
  ): { processedDocs: (DocumentItemList1 | undefined)[]; stats: { [key: string]: number } } {
    const stats = {
      totalIncoming: documentReferences.length,
      aboveZeroCount: 0,
      zeroCount: 0,
      subrosourcedMatched: 0,
      subrosourceUnmatched: 0,
      paternMatched: 0,
      patternUnmatched: 0
    };

    const processedDocs = documentReferences
      .filter(doc => {
        const hasValidCount = doc && typeof doc.count !== 'undefined' && doc.count > 0;
        if (hasValidCount) {
          stats.aboveZeroCount++;
        } else {
          stats.zeroCount++;
        }
        return hasValidCount;
      })
      .filter(doc => {
        const patternMatches = !doc?.lists?.some((list: DocumentListReference) =>
          !documentPattern.test(list?.name || '') && !list?.name?.includes('Subrosource')
        );
        if (patternMatches) {
          stats.subrosourcedMatched++;
        } else {
          stats.subrosourceUnmatched++;
        }
        return patternMatches;
      })
      .filter(doc => {
        if (!doc?.lists) {
          stats.patternUnmatched++;
          return false;
        }

        const hasMatchingList = doc.lists.some((list: DocumentListReference) => {
          const match = list?.name?.match(documentPattern);

          if (!match) {
            return false;
          }

          const requestNumber = parseInt(match[1], 10);
          (list as any).requestNumber = requestNumber;

          return true;
        });

        if (hasMatchingList) {
          stats.paternMatched++;
        } else {
          stats.patternUnmatched++;
        }

        return hasMatchingList;
      });

    return { processedDocs, stats };
}

  matchRequestsToDocuments(
    documentReferences: (DocumentItemList1 | undefined)[],
    request: RecordRetrievalRequestItem,
  ): DocumentReference[]  {

    const matchStats = {
      totalDocs: documentReferences.length,
      matchedByRequest: 0,
      matchedByList: 0,
      validReferences: 0
    };

    // Match the document to the request
    const requestMatchedDocs = documentReferences
      .filter(doc => {
        const matches = doc?.lists?.some((list: any) => 
          list.requestNumber === Number.parseInt(request.requestNumber??'0', 10)
        );
        if (matches) matchStats.matchedByRequest++;
        return matches;
      })
      .flatMap(doc => {
        const matchedLists = doc?.lists?.filter((list: any) => 
          list.requestNumber === Number.parseInt(request.requestNumber??'0', 10)
        ) || [];
        matchStats.matchedByList += matchedLists.length;
        
        return matchedLists.map((list: any) => {
          // Find all matching documents
          const docItems = doc?.documentItems?.filter(item => item.documentListId === list.id) || [];
          matchStats.validReferences += docItems.length;
          
          // Map each matching document to a DocumentReference
          return docItems.map(docItem => ({
            documentId: docItem.id,
            fileName: docItem.fileName,
            documentListId: list.id
          } as DocumentReference));
        }).flat();
      })
      .filter((doc): doc is DocumentReference => {
        return doc !== undefined;
      });

    this.observability?.trackEvent('document:processing:requestMatching', matchStats);

    return requestMatchedDocs
  }

  // here be skeletons
  mapRequestToTableData(
    request: RecordRetrievalRequestItem,
    idx: number,
    order: OrderItem,
    orderDetails: RootObject,
    documentReferences?: (DocumentItemList1 | undefined)[],
    vocabData?:any
  ): TableData {
    let requestMatchedDocs;
    let requestedRecordTypes;
    const vocabRecordTypesData = vocabData?.find((d: any) => d.vocabularyName === 'RecordTypes');
    if (vocabRecordTypesData) {
      requestedRecordTypes = _.filter(vocabRecordTypesData.vocabularyItems, item => {
        return _.some(request.scope, { recordTypeCode: item.code });
      })
    }
    if (documentReferences) {
      requestMatchedDocs = this.matchRequestsToDocuments(documentReferences, request);
    }

    const retrievalSubject = orderDetails.retrievalObjective?.parties?.find(
      p => p.roles?.some(
        r => r.roleCode === 'record-subject' && 
        r.requestNumbers?.includes(request.requestNumber ?? '')
      )
    );

    const providerSubject = orderDetails.retrievalObjective?.parties?.find(
      p => p.roles?.some(
        r => r.roleCode === 'records-custodian' &&
          r.requestNumbers?.includes(request.requestNumber ??'')
      )
    );

    const doctor = orderDetails.retrievalObjective?.parties?.find(
      p => p.roles?.some(
        r => r.roleCode === 'medical-provider' &&
          r.requestNumbers?.includes(request.requestNumber??'')
      )
    );

    const providerAddress = providerSubject?.party?.locators?.find(locator => locator.typeCode === 'location');
    const providerEmail = providerSubject?.party?.locators?.find(locator => locator.typeCode === 'email');

    const details: RecordDetails = {};
    request.scope?.forEach(scope => {
    // Map imaging details
    if (scope.recordTypeCode === 'diagnostic-images') {

      let diagnosticQualifiers : {
        diagnosticTypeCodes?: string,
        diagnosticFocusArea?: string,
        diagnosticComments?: string
      } = scope.qualifiers ?? {};

      details.imaging = {
        type: diagnosticQualifiers.diagnosticTypeCodes ?? '',
        bodyPart: diagnosticQualifiers.diagnosticFocusArea ?? '',
        comments: diagnosticQualifiers.diagnosticComments ?? ''
      };
    }

    // Map ambulance details
    if (scope.recordTypeCode === 'ambulance') {
      details.ambulance = {
        dateOfService: scope.beginDate || '',
        timeOfService: scope.endDate || '',
        pickupAddress: '',
        dropAddress: ''
      };
    }

    let subjectSsn = retrievalSubject?.roles?.find(role => role.roleCode === 'record-subject')?.recordSubject?.socialSecurityNumber;

    // Map employment details
    if (scope.recordTypeCode === 'employment') {
      let subjectSsn = retrievalSubject?.roles?.find(role => role.roleCode === 'record-subject')?.recordSubject?.socialSecurityNumber;
      details.employment = {
        ssn: subjectSsn ?? ''
      };
    }

    // Map insurance details
    if (scope.recordTypeCode === 'insurance') {
      let insuranceQualifiers : {
        insuranceClaimNumber?: string,
        insurancePolicyNumber?: string
      } = scope.qualifiers ?? {};

      details.insurance = {
        claimNumber: insuranceQualifiers.insuranceClaimNumber || '',
        policyNumber: insuranceQualifiers.insurancePolicyNumber || ''
      };
    }

    // Map IRS details
    if (scope.recordTypeCode === 'irs') {
      details.irs = {
        ssn: subjectSsn ?? ''
      };
    }

    // Map military details
    if (scope.recordTypeCode === 'military') {
      details.military = {
        branch: '',
        serviceNumber: ''
      };
    }

    // Map social security details
    if (scope.recordTypeCode === 'social-security') {
      details.socialSecurity = {
        ssn: subjectSsn ?? '',
        address: retrievalSubject?.party?.locators?.find(l => l.typeCode === 'location')?.location?.location?.usAddress?.lineOne || ''
      };
    }

    // Map union details
    if (scope.recordTypeCode === 'union') {
      details.union = {
        membershipNumber: ''
      };
    }

    // Map VA details
    if (scope.recordTypeCode === 'va') {
      details.va = {
        details: ''
      };
    }

    // Map other details
    if (scope.recordTypeCode === 'other') {
      details.other = {
        recordDescription: ''
      };
    }
  })

  let scopeItem = request.scope?.at(0);
  const recordTypes = requestedRecordTypes ? _.map(requestedRecordTypes, 'description') : _.map(request?.scope, 'recordTypeCode');

    return {
      id: Number.parseInt(request.requestNumber??'0', 10),
      formattedId: `${order.orderNumber}-${Number.parseInt(request.requestNumber??'0', 10)}` || `${order.id}-${Number.parseInt(request.requestNumber??'0', 10)}`,
      recordSubject: retrievalSubject?.party?.displayName || '--',
      providerName: providerSubject?.party?.displayName || '--',
      doctor: doctor?.party?.displayName || '--',
      address: providerAddress?.location?.location?.usAddress?.lineOne || '--',
      email: providerEmail?.email?.address || '--',
      // recordRequestType: scopeItem?.recordTypeCode || '--',
      recordRequestType: _.join(recordTypes, ',') || '--',
      // dateOfServices: this.formatDateRange(scopeItem?.beginDate, scopeItem?.endDate),
      dateOfServices:(scopeItem?.beginDate || scopeItem?.endDate) ? this.formatDateRange(scopeItem?.beginDate, scopeItem?.endDate) : 'Any or All',
      additionalInfo: {
        isCollapsed: true,
        details
      },
      documents: requestMatchedDocs ?? []
    };
  }

  private formatDateRange(beginDate?: string, endDate?: string): string {
    if (!beginDate && !endDate) return '--';
  
    const formatDate = (date?: string) => {
      if (!date) return '';
      const parsed = new Date(date);
      
      // Format as MM/dd/yyyy
      const month = (parsed.getMonth() + 1).toString().padStart(2, '0');
      const day = parsed.getDate().toString().padStart(2, '0');
      const year = parsed.getFullYear();
      
      return `${month}/${day}/${year}`;
    };
  
    const start = formatDate(beginDate);
    const end = formatDate(endDate);
  
    if (start && end) {
      return `${start} - ${end}`;
    }
    
    return start || end || '--';
  } 

  mapRequests(
    order: OrderItem, 
    orderDetails: RootObject,
    documentReferences?: (DocumentItemList1 | undefined)[],
    vocabData?: any
  ): TableData[] {
    return orderDetails.retrievalObjective?.requests?.map((request, idx) =>
      this.mapRequestToTableData(request, idx, order, orderDetails, documentReferences, vocabData)
    ) || [];
  }

  fetchDocuments(
    claim: ClaimItem,
    setLoading: (loading: boolean) => void
  ): Observable<DocumentItemList1[]> {
    const allDocumentLists = this.getClaimDocuments(claim);
    const documentObservables = this.getDocumentListObservables(allDocumentLists);

    return forkJoin(documentObservables).pipe(
      map(docs => docs.filter((doc): doc is DocumentItemList1 => doc !== undefined)),
      finalize(() => setLoading?.(false))
    );
  }

  processDocumentsAndMapRequests(
    claim: ClaimItem,
    order: OrderItem,
    orderDetails: RootObject,
    setLoading: (loading: boolean) => void
  ): Observable<TableData[]> {
    const allDocumentLists = this.getClaimDocuments(claim);
    const documentObservables = this.getDocumentListObservables(allDocumentLists);
    const documentPattern = /^[^-]+ - (\d+) - [^-]+ - [^-]+$/;

    return forkJoin(documentObservables).pipe(
      map(documentReferences => {
        const processedDocs = this.filterDocumentReferences(documentReferences, documentPattern);

        this.observability?.trackEvent('document:processing:filter:results', processedDocs.stats);

        return orderDetails.retrievalObjective?.requests?.map((request, idx) =>
          this.mapRequestToTableData(request, idx, order, orderDetails, documentReferences)
        ) || [];
      }),
      finalize(() => setLoading?.(false))
    );
  }
}
